import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getAppInsightsSummary, getCachedResponse, prefetch } from '../../api/client';
import type { AppInsightsSummary, Env } from '../../types';

const HELP =
  'Application Insights overview for the last 24 hours — total requests, average ' +
  'duration, failure rate, plus the top five thrown exceptions and the top five ' +
  'failing downstream dependencies (Salesforce, Semarchy, Auth0 etc.).';

function formatPercent(v?: number): string {
  if (v === undefined) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function formatDuration(ms?: number): string {
  if (ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)} s`;
  return `${(ms / 60_000).toFixed(2)} min`;
}

interface Props {
  env: Env;
  compact?: boolean;
  detailsHref?: string;
  windowHours?: number;
  refreshToken?: number;
}

export default function AppInsightsSummaryTile({ env, compact = false, detailsHref, windowHours = 24, refreshToken = 0 }: Props) {
  const [data, setData] = useState<AppInsightsSummary | null>(() =>
    getCachedResponse<AppInsightsSummary>('app-insights-summary', { env, windowHours }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<AppInsightsSummary>('app-insights-summary', { env, windowHours });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getAppInsightsSummary(env, windowHours)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // When used in compact mode on the overview, warm up the detail window too.
    if (compact) prefetch('app-insights-summary', { env, windowHours: 24 });
    return () => {
      cancelled = true;
    };
  }, [env, windowHours, refreshToken, compact]);

  const generatedFooter = data ? (
    <span className="tile-footer-row">
      <span>As of {new Date(data.generatedAt).toLocaleTimeString()} · 24h window</span>
      {detailsHref && (
        <a href={`#${detailsHref}`} className="tile-details-link" target="_blank" rel="noopener noreferrer">
          View details →
        </a>
      )}
    </span>
  ) : undefined;

  const failureRate = data?.failureRate ?? 0;
  const failureClass = failureRate > 0.05 ? 'bad' : failureRate > 0.01 ? 'warn' : 'ok';

  return (
    <TileCard
      title={`Application Insights — last 24h (${env.toUpperCase()})`}
      help={HELP}
      hideHelp={compact}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && (
        <>
          <dl className="stats-grid">
            <div>
              <dt>Requests</dt>
              <dd>{data.requestCount ?? 0}</dd>
            </div>
            <div>
              <dt>Avg</dt>
              <dd>{formatDuration(data.avgDurationMs)}</dd>
            </div>
            <div className={failureClass}>
              <dt>Fail rate</dt>
              <dd>{formatPercent(data.failureRate)}</dd>
            </div>
            <div className={(data.exceptionCount ?? 0) > 0 ? 'bad' : 'ok'}>
              <dt>Exceptions</dt>
              <dd>{data.exceptionCount ?? 0}</dd>
            </div>
          </dl>
          {!compact && (
          <div className="ai-detail-grid">
            <div>
              <h3 className="ai-detail-title">Top exceptions</h3>
              {(data.topExceptions?.length ?? 0) === 0 ? (
                <p className="tile-empty">None</p>
              ) : (
                <ul className="ai-list">
                  {data.topExceptions!.map((e, i) => (
                    <li key={`${e.type}-${i}`}>
                      <strong>{e.type || '(unknown)'}</strong>
                      <span className="muted"> × {e.count}</span>
                      {e.message && <div className="ai-msg">{e.message}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="ai-detail-title">Top failing dependencies</h3>
              {(data.topFailingDependencies?.length ?? 0) === 0 ? (
                <p className="tile-empty">None</p>
              ) : (
                <ul className="ai-list">
                  {data.topFailingDependencies!.map((d, i) => (
                    <li key={`${d.target}-${i}`}>
                      <strong>{d.target || '(unknown)'}</strong>
                      <span className="muted"> ({d.type}) × {d.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          )}
        </>
      )}
    </TileCard>
  );
}
